// Snap-to-track-edge. The render module computes inner/outer edge
// polylines into `state._trackEdges` whenever snap is "active" (walls
// mode, curb patches in surfaces mode, pit lane mode). This module
// turns a cursor world-position into the nearest snap-eligible edge
// point, optionally offset outward by half the curb thickness.

import { densifyClosedLoop, widthAt } from '../../shared/spline.js';
import { closestPointOnSegment } from '../../shared/geometry.js';
import { trackAlpha } from './state.js';

const SNAP_RADIUS_PX = 14;

// Recompute edges directly when needed by ops (the render-cached
// `state._trackEdges` may not have been populated yet in headless
// scenarios). Cheap — Mid-Ohio is ~24 × 61 = ~1500 points.
export function computeTrackEdges(state) {
  const wps = state.trackData.trackline.waypoints;
  if (wps.length < 3) return null;
  const SUBS = 24;
  const defaultWidth = state.trackData.trackline.width;
  const dense = densifyClosedLoop(wps, SUBS, trackAlpha(state));
  const inner = [], outer = [];
  for (let i = 0; i < dense.length; i++) {
    const a = dense[i];
    const b = dense[(i + 1) % dense.length];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    const nx = -dy / len, ny = dx / len;
    const segIdx = Math.floor(i / SUBS);
    const t = (i % SUBS) / SUBS;
    const half = widthAt(wps, defaultWidth, segIdx, t, true) / 2;
    inner.push({ x: a.x - nx * half, y: a.y - ny * half, perpX: -nx, perpY: -ny });
    outer.push({ x: a.x + nx * half, y: a.y + ny * half, perpX:  nx, perpY:  ny });
  }
  return { inner, outer };
}

export function snapToEdgeActive(state) {
  if (state.mode === 'walls') return true;
  if (state.mode === 'surfaces' && state.activePatchIdx >= 0) {
    const patch = state.trackData.surfacePatches[state.activePatchIdx];
    return patch?.surface === 'curb';
  }
  if (state.mode === 'pitlane' && state.trackData.pitLane) return true;
  return false;
}

// Outward offset to apply to a snap point. Curbs hug the track edge —
// offset by thickness/2 so the band sits flush instead of overlapping.
// Walls and pit lane snap with no offset.
function curbSnapOffset(state) {
  if (state.mode !== 'surfaces' || state.activePatchIdx < 0) return 0;
  const patch = state.trackData.surfacePatches[state.activePatchIdx];
  if (!patch || patch.surface !== 'curb') return 0;
  return (patch.thickness ?? 0.6) / 2;
}

function applySnapOffset(state, snapPoint) {
  if (!snapPoint) return null;
  const off = curbSnapOffset(state);
  if (off <= 0 || snapPoint.perpX == null) {
    return { x: snapPoint.x, y: snapPoint.y };
  }
  return {
    x: snapPoint.x + snapPoint.perpX * off,
    y: snapPoint.y + snapPoint.perpY * off,
  };
}

// Walk an edge polyline (closed loop) in line-segment chunks and find
// the closest point to worldPos. The perpendicular vector at the
// chosen point is interpolated linearly between the two endpoints'
// perp vectors so the curb offset stays well-defined between samples.
// Earlier point-only sampling left ~SUBS-spaced gaps that snap could
// not see when zoomed in close — same bug as the hit-tests had.
function nearestPointOnEdge(edge, worldPos) {
  let best = { dist: Infinity, point: null };
  if (edge.length < 2) return best;
  for (let i = 0; i < edge.length; i++) {
    const a = edge[i];
    const b = edge[(i + 1) % edge.length];
    const r = closestPointOnSegment(worldPos, a, b);
    if (r.dist < best.dist) {
      const perpX = a.perpX != null && b.perpX != null
        ? a.perpX + (b.perpX - a.perpX) * r.t
        : a.perpX ?? b.perpX ?? null;
      const perpY = a.perpY != null && b.perpY != null
        ? a.perpY + (b.perpY - a.perpY) * r.t
        : a.perpY ?? b.perpY ?? null;
      best = { dist: r.dist, point: { x: r.point.x, y: r.point.y, perpX, perpY } };
    }
  }
  return best;
}

// Find the nearest snap point within the screen-space radius. Returns
// null when not snap-active or no edge is in range. The cursor's
// distance is measured to the RAW edge point (not the post-offset
// point) so the snap radius behaves intuitively.
export function findSnapTarget(state, worldPos) {
  if (!snapToEdgeActive(state)) return null;
  const edges = state._trackEdges ?? computeTrackEdges(state);
  if (!edges) return null;
  const tol = SNAP_RADIUS_PX / state.camera.zoom;
  let best = { dist: Infinity, point: null };
  for (const list of [edges.inner, edges.outer]) {
    const r = nearestPointOnEdge(list, worldPos);
    if (r.dist < best.dist) best = r;
  }
  if (best.dist >= tol || !best.point) return null;
  return applySnapOffset(state, best.point);
}
